import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { pathFor } from "@/navConfig";
import { Reveal } from "@/components/Reveal";
import { speakerInitials, speakerSrc, speakerSrcSet } from "@/lib/speakerImg";

export const SpeakersStrip = () => {
  const { t, lang } = useLanguage();
  const s = t.speakersStrip;
  const merged = [
    ...(t.program.speakers.list || []),
    ...(t.program.roundtable?.speakersList || []),
  ];
  const seen = new Set();
  const list = merged
    .filter((sp) => (seen.has(sp.name) ? false : seen.add(sp.name)))
    .sort((a, b) =>
      a.name.trim().split(" ").pop().localeCompare(b.name.trim().split(" ").pop(), "cs")
    );

  return (
    <section id="speakers-strip" className="relative py-24 md:py-32 bg-[#010E3A] border-y border-white/5">
      <div className="container-page">
        <Reveal className="max-w-3xl">
          <span className="overline text-[#CEA26B]">{t.program.pageTabs.speakers}</span>
          <h2 className="mt-4 font-display font-bold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">
            {s.title}
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-5">
          {list.map((sp, i) => (
            <Reveal key={sp.name} delay={0.04 * i}>
              <Link
                to={`${pathFor("program", lang)}?tab=speakers`}
                data-testid={`speakers-strip-card-${i}`}
                className="group block h-full border border-white/10 bg-white/[0.02] overflow-hidden hover:border-[#CEA26B]/50 transition-colors duration-300"
              >
                <div className="aspect-square overflow-hidden bg-[#01154F]">
                  {sp.photo ? (
                    <img
                      src={speakerSrc(sp.photo, 640)}
                      srcSet={speakerSrcSet(sp.photo)}
                      sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 17vw"
                      alt={sp.name}
                      loading="lazy"
                      className="w-full h-full object-cover object-top grayscale-[25%] group-hover:grayscale-0 group-hover:scale-[1.04] transition-all duration-500"
                    />
                  ) : (
                    <div
                      data-testid={`speakers-strip-placeholder-${i}`}
                      className="flex h-full w-full items-center justify-center bg-[#0A2060] font-display text-4xl font-bold text-[#CEA26B]"
                      aria-label={sp.name}
                    >
                      {speakerInitials(sp.name)}
                    </div>
                  )}
                </div>
                <div className="p-4 border-t border-[#CEA26B]/60">
                  <h4 className="font-display font-bold text-white text-sm leading-snug">{sp.name}</h4>
                  <p className="mt-1 text-xs text-slate-400 leading-snug line-clamp-2">{sp.org}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-12 flex justify-center">
            <Link
              to={`${pathFor("program", lang)}?tab=speakers`}
              data-testid="speakers-strip-cta"
              className="group inline-flex items-center gap-2 border border-[#CEA26B] text-[#CEA26B] hover:bg-[#CEA26B] hover:text-[#01154F] px-8 py-4 font-semibold tracking-wide transition-colors"
            >
              {s.cta}
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
